<script setup lang="ts">
import { ref } from 'vue'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'

defineProps<{ open: boolean }>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  create: [data: {
    assignment_name: string
    description: string
    prev_search_efforts: string
    time_allocated: string
    size_of_assignment: string
  }]
}>()

const assignmentName = ref('')
const description = ref('')
const prevSearchEfforts = ref('')
const timeAllocated = ref('')
const sizeOfAssignment = ref('')
const submitting = ref(false)

function reset() {
  assignmentName.value = ''
  description.value = ''
  prevSearchEfforts.value = ''
  timeAllocated.value = ''
  sizeOfAssignment.value = ''
  submitting.value = false
}

async function handleSubmit() {
  if (!assignmentName.value.trim()) return
  submitting.value = true
  emit('create', {
    assignment_name: assignmentName.value.trim(),
    description: description.value.trim(),
    prev_search_efforts: prevSearchEfforts.value.trim(),
    time_allocated: timeAllocated.value.trim(),
    size_of_assignment: sizeOfAssignment.value.trim(),
  })
}

function handleOpenChange(val: boolean) {
  if (!val) reset()
  emit('update:open', val)
}
</script>

<template>
  <FosDialog :open="open" title="New Assignment" max-width="max-w-lg" @update:open="handleOpenChange">
    <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
      <!-- Assignment name -->
      <div class="flex flex-col gap-1">
        <label class="text-[10px] font-label uppercase tracking-widest text-outline">
          Assignment Name <span class="text-error">*</span>
        </label>
        <FosInput
          v-model="assignmentName"
          placeholder="e.g. Segment A, Grid 12"
          :disabled="submitting"
          required
          autofocus
        />
      </div>

      <!-- Description -->
      <div class="flex flex-col gap-1">
        <label class="text-[10px] font-label uppercase tracking-widest text-outline">Description</label>
        <FosTextarea
          v-model="description"
          placeholder="Area boundaries, terrain notes…"
          :disabled="submitting"
          :rows="2"
        />
      </div>

      <!-- Row: time allocated + size -->
      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-[10px] font-label uppercase tracking-widest text-outline">Time Allocated</label>
          <FosInput v-model="timeAllocated" placeholder="e.g. 4h" :disabled="submitting" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-[10px] font-label uppercase tracking-widest text-outline">Size</label>
          <FosInput v-model="sizeOfAssignment" placeholder="e.g. 2 km²" :disabled="submitting" />
        </div>
      </div>

      <!-- Prev search efforts -->
      <div class="flex flex-col gap-1">
        <label class="text-[10px] font-label uppercase tracking-widest text-outline">Previous Search Efforts</label>
        <FosTextarea
          v-model="prevSearchEfforts"
          placeholder="Prior coverage or results…"
          :disabled="submitting"
          :rows="2"
        />
      </div>

      <!-- Buttons inside form for submit semantics -->
      <div class="flex justify-end gap-2 mt-2">
        <FosButton type="button" variant="secondary" :disabled="submitting" @click="handleOpenChange(false)">
          Cancel
        </FosButton>
        <FosButton type="submit" variant="primary" :disabled="submitting || !assignmentName.trim()">
          <span v-if="submitting" class="w-4 h-4 border-2 border-white/30 border-t-white animate-spin inline-block" />
          <span v-else class="material-symbols-outlined text-[14px]">add</span>
          Create Assignment
        </FosButton>
      </div>
    </form>
  </FosDialog>
</template>
